import React from 'react'
import styled from 'styled-components'
import { GitBranch, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react'
import { Model } from '../../types/models/model'

// Styled components
const NodeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`

const NodePerformanceIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: rgba(34, 34, 34, 0.92);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
`

const NodeTitle = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const NodeSubtitle = styled.div`
  color: #a0a0a0;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const NodeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

const NodeInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;

  .label {
    color: #8c8c8c;
  }

  .value {
    color: #fff;
  }
`

const NodeBadge = styled.div<{
  type: 'parent' | 'branch' | 'child' | 'status'
}>`
  background: ${(props) => {
    switch (props.type) {
      case 'parent':
        return '#9147ff'
      case 'branch':
        return '#7c3aed'
      case 'child':
        return '#444'
      case 'status':
        return '#276749'
      default:
        return '#444'
    }
  }};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
`

// Metrics bar styled component
const MetricsBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 6px 0 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
`

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
  min-width: 60px;
`

const MetricLabel = styled.span`
  color: #a0a0a0;
  font-size: 10px;
`

const MetricValue = styled.span`
  font-weight: 600;
  color: #6ee7b7;
  font-size: 12px;
`

// Export the types and helper functions
export interface BoundingBoxMetrics {
  type: 'bounding_box'
  precision: number | null
  recall: number | null
  mAP: number | null
  f1?: number
}

export interface ClassificationMetrics {
  type: 'classification'
  accuracy: number
}

export type ModelMetrics = BoundingBoxMetrics | ClassificationMetrics

// Helper functions
export const getLatestMetrics = (model: Model): ModelMetrics | null => {
  if (!model?.metrics) {
    return null
  }

  if (model.annotation_type === 'classification') {
    const accuracy = model.metrics['validation:accuracy']
    return accuracy
      ? {
          type: 'classification',
          accuracy: accuracy as number,
        }
      : null
  } else {
    // Default to bounding box metrics
    const precision = model.metrics['validation:precision']
    const recall = model.metrics['validation:recall']
    const mAP = model.metrics['validation:mAP50-95']

    if (!precision && !recall && !mAP) {
      return null
    }

    const metrics: BoundingBoxMetrics = {
      type: 'bounding_box',
      precision: precision as number | null,
      recall: recall as number | null,
      mAP: mAP as number | null,
    }

    // Calculate F1 if we have both precision and recall
    if (metrics.precision && metrics.recall) {
      metrics.f1 = (2 * metrics.precision * metrics.recall) / (metrics.precision + metrics.recall)
    }

    return metrics
  }
}

export const getPerformanceComparison = (
  model: Model | undefined,
  parentModel: Model | undefined,
  projectAnnotationType?: string
): { improved: boolean | null; difference: number } => {
  if (!model || !parentModel) return { improved: null, difference: 0 }

  const modelMetrics = getLatestMetrics(model)
  const parentMetrics = getLatestMetrics(parentModel)

  if (!modelMetrics || !parentMetrics) return { improved: null, difference: 0 }

  if (projectAnnotationType === 'classification') {
    if (!('accuracy' in modelMetrics) || !('accuracy' in parentMetrics))
      return { improved: null, difference: 0 }
    const diff = modelMetrics.accuracy - parentMetrics.accuracy
    return { improved: diff > 0, difference: Math.abs(diff) }
  } else {
    // For bounding box, compare mAP
    if (!('mAP' in modelMetrics) || !('mAP' in parentMetrics))
      return { improved: null, difference: 0 }
    const diff = (modelMetrics.mAP || 0) - (parentMetrics.mAP || 0)
    return { improved: diff > 0, difference: Math.abs(diff) }
  }
}

export const getModelStatusBadge = (model: Model) => {
  if (model.state?.toLowerCase() === 'err' || model.deployment?.status?.toLowerCase() === 'err') {
    return (
      <NodeBadge type="status" style={{ background: '#9b2c2c' }}>
        ERROR
      </NodeBadge>
    )
  }

  if (model.state?.toLowerCase() === 'trained') {
    const deploymentStatus = model.deployment?.status?.toLowerCase()
    switch (deploymentStatus) {
      case 'creating':
        return (
          <NodeBadge type="status" style={{ background: '#975a16' }}>
            Deploying
          </NodeBadge>
        )
      case 'in_service':
        return (
          <NodeBadge type="status" style={{ background: '#285e61' }}>
            Deployed
          </NodeBadge>
        )
      default:
        return (
          <NodeBadge type="status" style={{ background: '#276749' }}>
            Trained
          </NodeBadge>
        )
    }
  }

  switch (model.state?.toLowerCase()) {
    case 'initialized':
      return (
        <NodeBadge type="status" style={{ background: '#2c5282' }}>
          Initialized
        </NodeBadge>
      )
    case 'training':
      return (
        <NodeBadge type="status" style={{ background: '#553c9a' }}>
          Training
        </NodeBadge>
      )
    case 'stopping':
      return (
        <NodeBadge type="status" style={{ background: '#9b2c2c' }}>
          Stopping
        </NodeBadge>
      )
    default:
      return null
  }
}

// Component props
export interface ModelTreeNodeProps {
  model: any
  isParent: boolean
  hasChildren: boolean
  parentModel?: any
  project?: any
  isHighlighted?: boolean
}

// Component
const ModelTreeNode: React.FC<ModelTreeNodeProps> = ({
  model,
  isParent,
  hasChildren,
  parentModel,
  project,
  isHighlighted = false,
}) => {
  const comparison = !isParent
    ? getPerformanceComparison(model, parentModel, project?.annotation_type)
    : null

  return (
    <NodeContent>
      {comparison && comparison.improved !== null && (
        <NodePerformanceIndicator>
          {comparison.improved ? (
            <ArrowUp size={16} color="#10B981" />
          ) : (
            <ArrowDown size={16} color="#EF4444" />
          )}
        </NodePerformanceIndicator>
      )}
      <NodeTitle>
        {isParent ? (
          <GitBranch size={16} />
        ) : hasChildren ? (
          <GitBranch size={14} />
        ) : (
          <ChevronRight size={14} />
        )}
        {model.name}
      </NodeTitle>
      <NodeSubtitle>
        <NodeBadge type={isParent ? 'parent' : hasChildren ? 'branch' : 'child'}>
          {isParent ? 'Parent Model' : hasChildren ? 'Branch Model' : 'Child Model'}
        </NodeBadge>
      </NodeSubtitle>
      <NodeInfo>
        <NodeInfoRow>
          <span className="label">Created</span>
          <span className="value">{new Date(model.created_at).toLocaleDateString()}</span>
        </NodeInfoRow>
        <NodeInfoRow>
          <span className="label">Status</span>
          <div>{getModelStatusBadge(model)}</div>
        </NodeInfoRow>
        {model.batch?.completed_content > 0 && (
          <NodeInfoRow>
            <span className="label">Predictions</span>
            <span className="value">{model.batch.completed_content}</span>
          </NodeInfoRow>
        )}
      </NodeInfo>
      <MetricsBar>
        {(() => {
          const metrics = getLatestMetrics(model)

          if (!metrics) {
            return (
              <MetricItem>
                <MetricLabel>No metrics available</MetricLabel>
              </MetricItem>
            )
          }

          if (metrics.type === 'classification') {
            return (
              <MetricItem>
                <MetricLabel>Accuracy</MetricLabel>
                <MetricValue>{(metrics.accuracy * 100).toFixed(1)}%</MetricValue>
              </MetricItem>
            )
          }

          return (
            <>
              {metrics?.precision && metrics.precision > 0 && (
                <MetricItem>
                  <MetricLabel>Precision</MetricLabel>
                  <MetricValue>{(metrics.precision * 100).toFixed(1)}%</MetricValue>
                </MetricItem>
              )}
              {metrics?.recall && metrics.recall > 0 && (
                <MetricItem>
                  <MetricLabel>Recall</MetricLabel>
                  <MetricValue>{(metrics.recall * 100).toFixed(1)}%</MetricValue>
                </MetricItem>
              )}
              {metrics.f1 && metrics.f1 > 0 && (
                <MetricItem>
                  <MetricLabel>F1 Score</MetricLabel>
                  <MetricValue>{(metrics.f1 * 100).toFixed(1)}%</MetricValue>
                </MetricItem>
              )}
              {metrics?.mAP && (
                <MetricItem>
                  <MetricLabel>mAP</MetricLabel>
                  <MetricValue>{(metrics.mAP * 100).toFixed(1)}%</MetricValue>
                </MetricItem>
              )}
            </>
          )
        })()}
      </MetricsBar>
    </NodeContent>
  )
}

export default ModelTreeNode

// Export CustomNode for use in SelectModelTreeModal
export const CustomNode = styled.div<{
  $isParent: boolean
  $isHighlighted?: boolean
  $selectable?: boolean
}>`
  padding: 16px;
  border-radius: 12px;
  background: ${(props) =>
    props.$isParent ? 'rgba(145, 71, 255, 0.15)' : 'rgba(34, 34, 34, 0.92)'};
  border: ${(props) =>
    props.$isHighlighted
      ? '2px solid #F59E0B'
      : props.$isParent
      ? '1px solid rgba(145, 71, 255, 0.5)'
      : '1px solid rgba(255, 255, 255, 0.1)'};
  color: white;
  font-size: 14px;
  width: 280px;
  text-align: left;
  cursor: ${(props) => (props.$selectable === false ? 'default' : 'pointer')};
  transition: all 0.2s ease;
  box-shadow: ${(props) => (props.$isHighlighted ? '0 0 20px rgba(245, 158, 11, 0.4)' : 'none')};

  &:hover {
    transform: ${(props) => (props.$selectable === false ? 'none' : 'translateY(-2px)')};
    box-shadow: ${(props) =>
      props.$isHighlighted
        ? '0 8px 20px rgba(245, 158, 11, 0.4)'
        : props.$selectable === false
        ? 'none'
        : '0 8px 16px rgba(0, 0, 0, 0.3)'};
    border-color: ${(props) =>
      props.$isHighlighted
        ? '#F59E0B'
        : props.$isParent
        ? 'rgba(145, 71, 255, 0.8)'
        : 'rgba(255, 255, 255, 0.3)'};
  }
`

// Export additional styled components for use in SelectModelTreeModal
export const PerformanceIndicator = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 2;
  background: rgba(34, 34, 34, 0.92);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);

  &.with-overlay {
    right: 25px;
  }
`

export const ClickableWrapper = styled.div`
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`

export const CardOverlayIcon = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 2;
  background: rgba(34, 34, 34, 0.92);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(145, 71, 255, 0.2);
    transform: scale(1.1);
  }
`

export const CardWrapper = styled.div`
  position: relative;
`
